import React from "react";
import FieldCard from "./FieldCard";
import InputField from "./InputField";

function CeilingSectionCard({ title, children }) {
  return (
    <div className="rounded-xl border border-gray-700 bg-gray-900 p-4">
      <div className="mb-3 text-sm font-semibold text-white">{title}</div>
      {children}
    </div>
  );
}

function createDefaultCeiling(base = {}) {
  return {
    id: crypto.randomUUID(),
    name: "",
    ceilingAreaM2: "",
    ceilingType: "standard",
    oldCeilingRemovalRequired: false,
    insulationRequired: false,
    finishRequired: true,
    ...base,
  };
}

function normalizeCeilingForEditor(section = {}) {
  return createDefaultCeiling({
    ...section,
    id: section.id || crypto.randomUUID(),
    ceilingType: section.ceilingType || "standard",
    oldCeilingRemovalRequired: !!section.oldCeilingRemovalRequired,
    insulationRequired: !!section.insulationRequired,
    finishRequired:
      section.finishRequired !== undefined ? !!section.finishRequired : true,
  });
}

export default function CeilingEditor({
  parsedData,
  setParsedData,
  errors = {},
}) {
  const ceilings =
    Array.isArray(parsedData?.ceilings) && parsedData.ceilings.length
      ? parsedData.ceilings.map(normalizeCeilingForEditor)
      : [
          normalizeCeilingForEditor({
            name: "",
            ceilingAreaM2: parsedData.ceilingAreaM2 ?? "",
            ceilingType: parsedData.ceilingType || "standard",
            oldCeilingRemovalRequired: !!parsedData.oldCeilingRemovalRequired,
            insulationRequired: !!parsedData.insulationRequired,
            finishRequired:
              parsedData.finishRequired !== undefined
                ? !!parsedData.finishRequired
                : true,
          }),
        ];

  const setCeilings = (nextCeilings) => {
    const normalizedCeilings = (Array.isArray(nextCeilings) ? nextCeilings : []).map(
      normalizeCeilingForEditor
    );

    setParsedData((prev) => ({
      ...prev,
      projectType: "ceiling",
      primaryProjectType: "ceiling",
      projectTypes: ["ceiling"],
      ceilings: normalizedCeilings,
      ceilingAreaM2: normalizedCeilings[0]?.ceilingAreaM2 ?? "",
      ceilingType: normalizedCeilings[0]?.ceilingType || "standard",
      oldCeilingRemovalRequired:
        !!normalizedCeilings[0]?.oldCeilingRemovalRequired,
      insulationRequired: !!normalizedCeilings[0]?.insulationRequired,
      finishRequired:
        normalizedCeilings[0]?.finishRequired !== undefined
          ? !!normalizedCeilings[0]?.finishRequired
          : true,
    }));
  };

  const addCeiling = () => {
    setCeilings([...ceilings, createDefaultCeiling()]);
  };

  const updateCeiling = (index, field, value) => {
    setCeilings(
      ceilings.map((section, i) =>
        i === index
          ? normalizeCeilingForEditor({ ...section, [field]: value })
          : section
      )
    );
  };

  const removeCeiling = (index) => {
    const nextCeilings = ceilings.filter((_, i) => i !== index);

    setCeilings(nextCeilings.length ? nextCeilings : [createDefaultCeiling()]);
  };

  return (
    <div className="space-y-4">
      <div className="rounded-lg border border-gray-700 bg-gray-900 px-4 py-3 text-sm text-gray-400">
        Define one or more ceiling sections. Enter area and expected work type for
        each section separately.
      </div>

      <div className="flex flex-wrap gap-3">
        <button
          type="button"
          onClick={addCeiling}
          className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700"
        >
          Add ceiling section
        </button>
      </div>

      <div className="space-y-4">
        {ceilings.map((section, index) => (
          <CeilingSectionCard
            key={section.id || `ceiling-${index}`}
            title={section.name?.trim() || `Ceiling section ${index + 1}`}
          >
            <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
              <FieldCard
                label="Section name"
                warning="Optional internal name like Living room ceiling, Hall ceiling, Office ceiling."
              >
                <InputField
                  value={section.name ?? ""}
                  onChange={(e) => updateCeiling(index, "name", e.target.value)}
                  placeholder="e.g. Hall ceiling"
                />
              </FieldCard>

              <FieldCard
                label="Ceiling area"
                unit="m²"
                error={
                  errors[`ceilings.${index}`] ||
                  (index === 0 ? errors.ceilingAreaM2 : "")
                }
                warning="Total ceiling surface for this section."
              >
                <InputField
                  type="number"
                  value={section.ceilingAreaM2 ?? ""}
                  onChange={(e) =>
                    updateCeiling(
                      index,
                      "ceilingAreaM2",
                      e.target.value === "" ? "" : Number(e.target.value)
                    )
                  }
                  placeholder="e.g. 38"
                />
              </FieldCard>

              <FieldCard
                label="Ceiling type"
                warning="Choose the main type of work or structure."
              >
                <select
                  value={section.ceilingType || "standard"}
                  onChange={(e) =>
                    updateCeiling(index, "ceilingType", e.target.value)
                  }
                  className="h-10 w-full rounded-lg border border-gray-600 bg-gray-800 px-3 py-2 text-sm text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="standard">Standard ceiling</option>
                  <option value="plasterboard">Plasterboard ceiling</option>
                  <option value="suspended">Suspended ceiling</option>
                  <option value="acoustic">Acoustic ceiling</option>
                  <option value="other">Other</option>
                </select>
              </FieldCard>

              <FieldCard
                label="Remove old ceiling?"
                warning="Enable if demolition or disposal is required."
              >
                <select
                  value={section.oldCeilingRemovalRequired ? "yes" : "no"}
                  onChange={(e) =>
                    updateCeiling(
                      index,
                      "oldCeilingRemovalRequired",
                      e.target.value === "yes"
                    )
                  }
                  className="h-10 w-full rounded-lg border border-gray-600 bg-gray-800 px-3 py-2 text-sm text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="no">No</option>
                  <option value="yes">Yes</option>
                </select>
              </FieldCard>

              <FieldCard
                label="Insulation required?"
                warning="Enable if ceiling insulation should be included."
              >
                <select
                  value={section.insulationRequired ? "yes" : "no"}
                  onChange={(e) =>
                    updateCeiling(
                      index,
                      "insulationRequired",
                      e.target.value === "yes"
                    )
                  }
                  className="h-10 w-full rounded-lg border border-gray-600 bg-gray-800 px-3 py-2 text-sm text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="no">No</option>
                  <option value="yes">Yes</option>
                </select>
              </FieldCard>

              <FieldCard
                label="Finish required?"
                warning="Enable if finishing work or finish materials should be included."
              >
                <select
                  value={section.finishRequired === false ? "no" : "yes"}
                  onChange={(e) =>
                    updateCeiling(
                      index,
                      "finishRequired",
                      e.target.value === "yes"
                    )
                  }
                  className="h-10 w-full rounded-lg border border-gray-600 bg-gray-800 px-3 py-2 text-sm text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="yes">Yes</option>
                  <option value="no">No</option>
                </select>
              </FieldCard>

              {index === 0 && (
                <FieldCard
                  label="Finish level"
                  warning="Use premium if smoother finish and tighter detailing is needed."
                >
                  <select
                    value={parsedData.finishLevel || "standard"}
                    onChange={(e) =>
                      setParsedData((prev) => ({
                        ...prev,
                        projectType: "ceiling",
                        primaryProjectType: "ceiling",
                        projectTypes: ["ceiling"],
                        finishLevel: e.target.value,
                        ceilings,
                      }))
                    }
                    className="h-10 w-full rounded-lg border border-gray-600 bg-gray-800 px-3 py-2 text-sm text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="standard">Standard</option>
                    <option value="premium">Premium</option>
                  </select>
                </FieldCard>
              )}
            </div>

            <div className="mt-4 flex justify-end">
              <button
                type="button"
                onClick={() => removeCeiling(index)}
                className="rounded-lg bg-red-600 px-3 py-2 text-sm font-medium text-white hover:bg-red-700"
              >
                Remove section
              </button>
            </div>
          </CeilingSectionCard>
        ))}
      </div>
    </div>
  );
}